import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Grid,
  Alert,
  Snackbar,
  CircularProgress,
  IconButton,
  LinearProgress
} from '@mui/material';
import { doc, onSnapshot, setDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import LinkIcon from '@mui/icons-material/Link';
import {
  NEARBY_ACTIVITIES_DEFAULT_DESCRIPTION,
  NEARBY_ACTIVITIES_DEFAULTS,
  NEARBY_ACTIVITIES_DEFAULT_TITLE,
  mergeWithNearbyActivityDefaults,
} from '../../data/nearbyActivitiesDefaults';

const GOLD = '#d4af37';

interface ActivityItem {
  id: string;
  name: string;
  type: string;
  duration: string;
  location: string;
  description: string;
  difficulty?: string;
  imageUrl?: string;
}

interface ActivitiesContent {
  title: string;
  description: string;
  activities: ActivityItem[];
}

const isBase64Image = (url?: string) =>
  typeof url === 'string' && url.startsWith('data:image/');

const compressImageToBase64 = (
  file: File,
  maxWidth = 960,
  maxHeight = 720,
  quality = 0.72,
  maxBase64Length = 110_000
): Promise<string> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (width > maxWidth || height > maxHeight) {
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Failed to initialize image canvas'));
        return;
      }

      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, width, height);

      let q = quality;
      let base64 = canvas.toDataURL('image/jpeg', q);
      while (base64.length > maxBase64Length && q > 0.45) {
        q -= 0.05;
        base64 = canvas.toDataURL('image/jpeg', q);
      }

      let workingCanvas = canvas;
      while (base64.length > maxBase64Length && workingCanvas.width > 320 && workingCanvas.height > 240) {
        const smallCanvas = document.createElement('canvas');
        const scale = 0.82;
        smallCanvas.width = Math.round(workingCanvas.width * scale);
        smallCanvas.height = Math.round(workingCanvas.height * scale);
        const sCtx = smallCanvas.getContext('2d');
        if (!sCtx) {
          reject(new Error('Failed to initialize compression canvas'));
          return;
        }
        sCtx.imageSmoothingEnabled = true;
        sCtx.imageSmoothingQuality = 'high';
        sCtx.drawImage(workingCanvas, 0, 0, smallCanvas.width, smallCanvas.height);
        q = Math.max(0.45, q - 0.06);
        base64 = smallCanvas.toDataURL('image/jpeg', q);
        workingCanvas = smallCanvas;
      }

      resolve(base64);
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };
    img.src = url;
  });

const ActivitiesNearbyManager = () => {
  const [content, setContent] = useState<ActivitiesContent>({
    title: NEARBY_ACTIVITIES_DEFAULT_TITLE,
    description: NEARBY_ACTIVITIES_DEFAULT_DESCRIPTION,
    activities: NEARBY_ACTIVITIES_DEFAULTS.map((activity) => ({ ...activity })),
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [uploadingActivityId, setUploadingActivityId] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const activityFileInputRef = useRef<HTMLInputElement>(null);
  const pendingActivityId = useRef<string | null>(null);
  const activityFocusHandler = useRef<(() => void) | null>(null);

  const loadContent = useCallback(() => {
    try {
      return onSnapshot(doc(db, 'nearby_content', 'activities'), (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data() as ActivitiesContent;
          const nextActivities = mergeWithNearbyActivityDefaults(
            Array.isArray(data.activities) ? data.activities : []
          );
          setContent({
            title: data.title || NEARBY_ACTIVITIES_DEFAULT_TITLE,
            description: data.description || NEARBY_ACTIVITIES_DEFAULT_DESCRIPTION,
            activities: nextActivities.map((activity) => ({ ...activity })),
          });
        } else {
          setContent({
            title: NEARBY_ACTIVITIES_DEFAULT_TITLE,
            description: NEARBY_ACTIVITIES_DEFAULT_DESCRIPTION,
            activities: NEARBY_ACTIVITIES_DEFAULTS.map((activity) => ({ ...activity })),
          });
        }
        setLoading(false);
      });
    } catch (error) {
      console.error('Error loading content:', error);
      setLoading(false);
      showSnackbar('Failed to load content', 'error');
      return () => {};
    }
  }, []);

  useEffect(() => {
    const unsubscribe = loadContent();
    return () => unsubscribe();
  }, [loadContent]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await setDoc(doc(db, 'nearby_content', 'activities'), content);
      showSnackbar('Activities data saved successfully', 'success');
    } catch (error) {
      console.error('Error saving:', error);
      showSnackbar('Failed to save content', 'error');
    } finally {
      setSaving(false);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleAddActivity = () => {
    const newActivity: ActivityItem = {
      id: Date.now().toString(),
      name: 'New Activity',
      type: 'Adventure',
      duration: '',
      location: '',
      description: '',
      difficulty: 'Easy',
      imageUrl: '',
    };
    setContent({
      ...content,
      activities: [...content.activities, newActivity]
    });
  };

  const handleActivityChange = (id: string, field: keyof ActivityItem, value: string) => {
    setContent({
      ...content,
      activities: content.activities.map(activity =>
        activity.id === id ? { ...activity, [field]: value } : activity
      )
    });
  };

  const handleDeleteActivity = (id: string) => {
    setContent({
      ...content,
      activities: content.activities.filter(activity => activity.id !== id)
    });
  };

  const handleActivityFileUpload = async (id: string, file: File) => {
    if (!file.type.startsWith('image/')) {
      showSnackbar('Please select an image file', 'error');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      showSnackbar('Please select an image smaller than 8MB', 'error');
      return;
    }
    setUploadingActivityId(id);
    setUploadProgress(10);

    try {
      setUploadProgress(30);
      const base64 = await compressImageToBase64(file);
      setUploadProgress(90);

      setContent((prev) => ({
        ...prev,
        activities: prev.activities.map((item) =>
          item.id === id ? { ...item, imageUrl: base64 } : item
        ),
      }));

      setUploadProgress(100);
      const originalKB = Math.round(file.size / 1024);
      const compressedKB = Math.round((base64.length * 3) / 4 / 1024);
      setTimeout(() => {
        setUploadingActivityId(null);
        setUploadProgress(0);
        showSnackbar(
          `Image compressed (${originalKB}KB \u2192 ${compressedKB}KB). Click "Save Changes" to persist.`,
          'success'
        );
      }, 400);
    } catch (error) {
      console.error('Activity image upload failed:', error);
      showSnackbar('Failed to process image', 'error');
      setUploadingActivityId(null);
      setUploadProgress(0);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress sx={{ color: GOLD }} />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
            Activities Manager
          </Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            Manage nearby activities content
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            startIcon={<RefreshIcon />}
            onClick={loadContent}
            disabled={loading}
            variant="outlined"
            sx={{ borderColor: GOLD, color: GOLD, '&:hover': { bgcolor: `${GOLD}15` } }}
          >
            Refresh
          </Button>
          <Button
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving}
            variant="contained"
            sx={{ bgcolor: GOLD, color: '#000', fontWeight: 700 }}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </Box>

      <Paper elevation={0} sx={{ p: 3, mb: 3, border: `2px solid ${GOLD}`, borderRadius: 2, bgcolor: '#fafaf8' }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: GOLD, mb: 2 }}>
          🎯 Section Settings
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Section Title"
              value={content.title}
              onChange={(e) => setContent({ ...content, title: e.target.value })}
              size="small"
              sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Section Description"
              value={content.description}
              onChange={(e) => setContent({ ...content, description: e.target.value })}
              multiline
              rows={2}
              sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper elevation={0} sx={{ p: 3, mb: 3, border: `2px solid ${GOLD}`, borderRadius: 2, bgcolor: '#fafaf8' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: GOLD }}>
            🏃 Activities ({content.activities.length})
          </Typography>
          <Button
            startIcon={<AddIcon />}
            onClick={handleAddActivity}
            variant="contained"
            sx={{ bgcolor: GOLD, color: '#000', fontWeight: 700 }}
          >
            Add Activity
          </Button>
        </Box>

        <Grid container spacing={2}>
          {content.activities.map((activity) => (
            <Grid item xs={12} key={activity.id}>
              <Paper sx={{ p: 2, bgcolor: '#fff', border: `1px solid ${GOLD}` }}>
                <Grid container spacing={2} alignItems="flex-start">
                  <Grid item xs={12} sm={5}>
                    <TextField
                      fullWidth
                      label="Activity Name"
                      value={activity.name}
                      onChange={(e) => handleActivityChange(activity.id, 'name', e.target.value)}
                      size="small"
                      sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      label="Type"
                      value={activity.type}
                      onChange={(e) => handleActivityChange(activity.id, 'type', e.target.value)}
                      size="small"
                      sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      label="Difficulty"
                      value={activity.difficulty || ''}
                      onChange={(e) => handleActivityChange(activity.id, 'difficulty', e.target.value)}
                      size="small"
                      placeholder="Easy, Moderate, Hard"
                      sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={1}>
                    <IconButton
                      onClick={() => handleDeleteActivity(activity.id)}
                      color="error"
                      sx={{ width: '100%' }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Duration"
                      value={activity.duration}
                      onChange={(e) => handleActivityChange(activity.id, 'duration', e.target.value)}
                      size="small"
                      placeholder="e.g., 3-4 hours"
                      sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Location"
                      value={activity.location}
                      onChange={(e) => handleActivityChange(activity.id, 'location', e.target.value)}
                      size="small"
                      sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Description"
                      value={activity.description}
                      onChange={(e) => handleActivityChange(activity.id, 'description', e.target.value)}
                      multiline
                      rows={2}
                      size="small"
                      sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={8}>
                    <TextField
                      fullWidth
                      label="Image URL"
                      value={isBase64Image(activity.imageUrl) ? '(Uploaded image)' : activity.imageUrl || ''}
                      onChange={(e) => handleActivityChange(activity.id, 'imageUrl', e.target.value)}
                      size="small"
                      disabled={isBase64Image(activity.imageUrl)}
                      placeholder="https://example.com/activity.jpg"
                      sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                      <Button
                        variant="outlined"
                        startIcon={<CloudUploadIcon />}
                        disabled={uploadingActivityId === activity.id}
                        onClick={() => {
                          pendingActivityId.current = activity.id;
                          activityFileInputRef.current?.click();
                          const onFocus = () => {
                            setTimeout(() => {
                              if (pendingActivityId.current === activity.id) {
                                pendingActivityId.current = null;
                              }
                              setUploadingActivityId((prev) => prev === activity.id ? null : prev);
                              setUploadProgress(0);
                            }, 300);
                            window.removeEventListener('focus', onFocus);
                            activityFocusHandler.current = null;
                          };
                          activityFocusHandler.current = onFocus;
                          window.addEventListener('focus', onFocus);
                        }}
                        sx={{
                          borderColor: GOLD,
                          color: GOLD,
                          fontWeight: 600,
                          fontSize: '0.72rem',
                          textTransform: 'none',
                          height: 34,
                          '&:hover': { bgcolor: `${GOLD}15`, borderColor: GOLD },
                        }}
                      >
                        {uploadingActivityId === activity.id ? 'Uploading...' : 'Upload Image'}
                      </Button>
                      {activity.imageUrl && !isBase64Image(activity.imageUrl) && (
                        <Button
                          size="small"
                          startIcon={<DeleteIcon />}
                          onClick={() => handleActivityChange(activity.id, 'imageUrl', '')}
                          sx={{ textTransform: 'none', fontSize: '0.7rem', color: '#888' }}
                        >
                          Clear Image
                        </Button>
                      )}
                      {isBase64Image(activity.imageUrl) && (
                        <Button
                          size="small"
                          startIcon={<LinkIcon />}
                          onClick={() => handleActivityChange(activity.id, 'imageUrl', '')}
                          sx={{ textTransform: 'none', fontSize: '0.7rem', color: '#888' }}
                        >
                          Clear & use URL
                        </Button>
                      )}
                    </Box>
                    {uploadingActivityId === activity.id && (
                      <LinearProgress
                        variant="determinate"
                        value={uploadProgress}
                        sx={{ mt: 0.5, borderRadius: 1, '& .MuiLinearProgress-bar': { bgcolor: GOLD } }}
                      />
                    )}
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    {activity.imageUrl && (
                      <Box sx={{ width: '100%', height: 100, borderRadius: '8px', overflow: 'hidden', border: '1px solid #eee' }}>
                        <img src={activity.imageUrl} alt={activity.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Paper>

      <input
        type="file"
        accept="image/*"
        ref={activityFileInputRef}
        style={{ display: 'none' }}
        onChange={(e) => {
          if (activityFocusHandler.current) {
            window.removeEventListener('focus', activityFocusHandler.current);
            activityFocusHandler.current = null;
          }
          const file = e.target.files?.[0];
          const targetId = pendingActivityId.current;
          if (file && targetId) {
            handleActivityFileUpload(targetId, file);
          }
          pendingActivityId.current = null;
          e.target.value = '';
        }}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ActivitiesNearbyManager;
